import { useCurrentUserStore } from '@infuse/iam/user/store';

export type PermissionCheckType = 'any' | 'all';

export function hasRequiredPermissions(
  permissionIds: string[],
  check: PermissionCheckType = 'all',
  useSubscriptionId: boolean
): boolean {
  const currentUserStore = useCurrentUserStore();

  if (!currentUserStore.enabled) {
    return true;
  }

  if (!permissionIds.length) {
    return true;
  }

  if (check === 'any') {
    return permissionIds.some((id) =>
      currentUserStore.hasPermission(id, useSubscriptionId ? currentUserStore.mainSubscriptionId : undefined)
    );
  }
  return permissionIds.every((id) =>
    currentUserStore.hasPermission(id, useSubscriptionId ? currentUserStore.mainSubscriptionId : undefined)
  );
}

export function isPermissionAllowed(meta: Record<string, unknown>): boolean {
  const { permissionIds, permissionCheck, useSubscriptionId } = getPermissionMeta(meta);
  return hasRequiredPermissions(permissionIds, permissionCheck, useSubscriptionId);
}

export function isApplicationAllowed(): boolean {
  const { enabled, permissions, user } = useCurrentUserStore();

  if (!enabled) {
    return true;
  }

  // These lib files do not like the Vue import.meta.env, but it does in fact exist
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const {
    VITE_UTILS_APP_PERMISSION_PREFIX = '',
    VITE_DISALLOWED_ROLES = '',
    VITE_ALLOWED_PERMISSION_PREFIXES = ''
  } = import.meta.env;

  try {
    const disallowedRoles = VITE_DISALLOWED_ROLES.split(',');
    if (user?.roleLabels.some((role) => disallowedRoles.includes(role.label))) {
      // Do not let the user in when the specific role is excluded from entering
      return false;
    }

    // Check if the user has a permission for this specific application, for example for Infuse CPMS, user should have a permission starting with cpms.
    const directAccess = permissions.some((permission) =>
      permission.permissionId.startsWith(VITE_UTILS_APP_PERMISSION_PREFIX)
    );
    if (directAccess) {
      return true;
    }

    // Direct access is not granted, check for allowed permission prefixes
    const allowedPermissionPrefixes = VITE_ALLOWED_PERMISSION_PREFIXES.split(',')
      .map((prefix: string) => prefix.trim())
      .filter((prefix: string) => prefix.length > 0);

    // Direct access is not granted, but no allowed permission prefixes are set, so deny access
    if (!allowedPermissionPrefixes.length) {
      return false;
    }

    return permissions.some((permission) =>
      allowedPermissionPrefixes.some((prefix: string) => permission.permissionId.startsWith(prefix))
    );
  } catch (err) {
    // catch errors and return "not allowed" - without the try/catch block above a broken login would just Error and end up on a static white screen
    console.error(err);
    return false;
  }
}

export function isUserDeactivated(): boolean {
  const { enabled, user } = useCurrentUserStore();
  if (!enabled) {
    return false;
  }
  if (!user) {
    return false;
  }
  return !user.active;
}

/**
 * Returns true when the authenticated user is scoped to an organisation or
 * tenant but has not yet been assigned any role. In that case the auth guard
 * routes to /request-access instead of the generic not-allowed page so the
 * user can request access from an administrator.
 */
export function shouldRequestAccess(): boolean {
  const { enabled, user } = useCurrentUserStore();
  if (!enabled || !user) {
    return false;
  }
  const hasNoRole = (user.roleLabels?.length ?? 0) === 0;
  return hasNoRole;
}

function getPermissionMeta(meta: Record<string, any>): {
  permissionIds: string[];
  permissionCheck: PermissionCheckType;
  useSubscriptionId?: boolean;
} {
  return {
    permissionIds: (meta?.permissionIds as string[]) || [],
    permissionCheck: (meta?.permissionCheck as PermissionCheckType) || 'any',
    useSubscriptionId: (meta?.useSubscriptionId as boolean) || false
  };
}
